import { useState } from 'react'
import { CheckOutlined, SwapOutlined } from '@ant-design/icons'
import { App, Button, Dropdown, Typography } from 'antd'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { translateError } from '@/shared/api/errors'
import { queryKeys } from '@/shared/api/queryKeys'
import { fetchMyTenants } from '@/shared/auth/authApi'
import { useAuth } from '@/shared/auth/useAuth'

/**
 * Yuqori paneldagi tashkilot tanlagichi.
 *
 * <h2>Nega tanlagich, sozlamalar sahifasidagi maydon emas</h2>
 * Bu sozlama emas — KONTEKST. Operator "qaysi tashkilotda ishlayapman"
 * savoliga har ekranda javob ko'rishi kerak, va almashtirish o'sha
 * javobning yonida turishi kerak. Sozlamalar ichiga yashirilsa,
 * noto'g'ri tashkilotda abonent yaratish xatosi saqlanib qolardi —
 * aynan shu xato uchun tashkilot nomi allaqachon sarlavhada turibdi.
 *
 * <h2>⚠️ Bitta a'zolikda UMUMAN ko'rsatilmaydi</h2>
 * Foydalanuvchilarning aksariyati bitta tashkilotda ishlaydi. Ular uchun
 * bu tugma hech qachon bosilmaydigan, lekin har kuni ko'riladigan
 * shovqin bo'lardi — o'rniga oddiy matn qoladi (avvalgidek).
 *
 * <h2>⚠️ O'tgandan keyin BOSH SAHIFAGA yo'naltiriladi</h2>
 * Joriy manzil boshqa tashkilotning yozuvi bo'lishi mumkin
 * (`/subscribers/<uuid>`). O'sha manzilda qolish yangi tashkilotda
 * "topilmadi" ekranini berardi va operator almashtirish ishlamadi deb
 * o'ylardi. Kesh esa `AuthProvider.switchTenant` da tozalanadi — bu
 * yerda takrorlanmaydi.
 */
export function TenantSwitcher() {
  const { t } = useTranslation()
  const { user, switchTenant } = useAuth()
  const { message } = App.useApp()
  const navigate = useNavigate()
  const [switching, setSwitching] = useState(false)

  const tenants = useQuery({
    queryKey: queryKeys.myTenants,
    queryFn: fetchMyTenants,
    enabled: Boolean(user),
    // A'zoliklar to'plami kun davomida deyarli o'zgarmaydi, lekin
    // `Infinity` ham qo'yilmadi: admin yangi tashkilotga qo'shsa,
    // xodim uni qayta kirmasdan ko'rishi kerak
    staleTime: 5 * 60_000,
    retry: false,
  })

  const list = tenants.data ?? []
  const currentName = user?.tenant.name ?? ''

  /**
   * ⚠️ NOM KESILADI — 160px dan keyin uch nuqta.
   *
   * Yuqori panel tor ekranda (390px) siqiladi va har bir element o'z
   * eni uchun kurashadi (A7 o'lchovi). Tashkilot nomi uzunligi
   * cheklanmagan maydon: "Afghan Wireless Communication Company" kabi
   * nom butun panelni surib yuborardi va sahifa sarlavhasi qolgan
   * bo'sh joydan mahrum bo'lardi.
   *
   * To'liq nom `title` da qoladi — sichqoncha ustiga kelganda ko'rinadi.
   */
  const clamp = {
    maxWidth: 160,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  }

  // Bitta (yoki noma'lum) a'zolik — tanlov yo'q, faqat nom
  if (list.length < 2) {
    return currentName ? (
      <Typography.Text
        type="secondary"
        title={currentName}
        style={{ marginInlineEnd: 8, display: 'inline-block', verticalAlign: 'middle', ...clamp }}
      >
        {currentName}
      </Typography.Text>
    ) : null
  }

  const handleSwitch = (tenantId: string) => {
    if (tenantId === user?.tenant.id) return
    setSwitching(true)
    void switchTenant(tenantId)
      .then(() => {
        navigate('/', { replace: true })
        void message.success(t('auth.tenantSwitcher.done'))
      })
      .catch((error: unknown) => void message.error(translateError(error, t)))
      .finally(() => setSwitching(false))
  }

  return (
    <Dropdown
      trigger={['click']}
      menu={{
        selectable: false,
        items: list.map((tenant) => ({
          key: tenant.id,
          // Joriy tashkilot ham ro'yxatda QOLADI (o'chirilmaydi):
          // ro'yxatdan yo'qolsa "qayerdaman" savoli yana paydo bo'lardi
          icon: tenant.id === user?.tenant.id ? <CheckOutlined /> : <SwapOutlined />,
          disabled: tenant.id === user?.tenant.id,
          label: (
            <span>
              {tenant.name}{' '}
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {tenant.code}
                {tenant.currency ? ` · ${tenant.currency}` : ''}
              </Typography.Text>
            </span>
          ),
          onClick: () => handleSwitch(tenant.id),
        })),
      }}
    >
      <Button
        type="text"
        loading={switching}
        aria-label={t('auth.tenantSwitcher.label')}
        title={currentName}
        style={clamp}
      >
        {currentName}
      </Button>
    </Dropdown>
  )
}
